import * as React from 'react';
import {
  TResetPassword,
  TResetPasswordFormValues,
} from './ResetPassword.types';
import './ResetPassword.styles.scss';
import { RESET_PASSWORD_FORM_DEFAULT_VALUES } from './resetPassword.constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { resetPasswordSchema } from './ResetPassword.schema';
import { Controller, useForm } from 'react-hook-form';
import { Button, TextInput } from 'components';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { useAppDispatch, useAppSelector } from 'store/store';
import { resetPasswordAsyncThunk } from 'store/users/users.action';
import { useUrlParams } from 'hooks/useUpdateUrlParams';
import { useNavigate } from 'react-router';
import { APP_ROUTES } from 'constants/routes';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';

export const ResetPassword: TResetPassword = () => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openModal } = useModals();
  const { getAllParams } = useUrlParams();

  const token = getAllParams().token as string;

  const { isLoading: isResetPasswordLoading } = useAppSelector(
    (state) => state.users.resetPassword
  );

  const { control, handleSubmit } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: RESET_PASSWORD_FORM_DEFAULT_VALUES,
    resolver: yupResolver(resetPasswordSchema),
  });

  const onSubmit = (data: TResetPasswordFormValues) => {
    if (token) {
      dispatch(
        resetPasswordAsyncThunk({
          token,
          password: data.password,
          onSuccess: () => {
            navigate(APP_ROUTES.HOME);
            openModal(MODALS.LOGIN);
          },
        })
      );
    }
  };

  React.useEffect(() => {
    if (!token) {
      navigate(APP_ROUTES.HOME);
    }
  }, [token, navigate]);

  return (
    <div className="reset-password">
      <div className="reset-password__container">
        <form
          className="reset-password__container__form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <h4>{localize('resetpassword.form.title') as string}</h4>
          <div className="reset-password__container__form__input">
            <Controller
              control={control}
              name="password"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  fullWidth
                  type="password"
                  label={localize('resetpassword.form.password.label')}
                  placeholder={
                    localize(
                      'resetpassword.form.password.placeholder'
                    ) as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="reset-password__container__form__input">
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  fullWidth
                  type="password"
                  label={localize('resetpassword.form.confirmpassword.label')}
                  placeholder={
                    localize(
                      'resetpassword.form.confirmpassword.placeholder'
                    ) as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
          </div>
          <div className="reset-password__container__form__button">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isResetPasswordLoading || !token}
              fullWidth
            >
              {localize('resetpassword.form.button.submit') as string}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
